import type { UserPrivate } from '@repo/types/users';

import { usePostSignup } from '@repo/api/auth/usePostSignup';
import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { AlertCircle, CalendarIcon } from 'lucide-react';
import { useNavigate } from 'react-router';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneNumberInput } from '@/components/ui/input/phone-number';
import { useIsDefaultLocation } from '@/hooks/default-location';
import { useSignupForm } from '@/hooks/signup-form';

interface SignupFormProps {
  authToken?: string;
  signupData?: UserPrivate;
}

export function SignupForm(signupFormProps: SignupFormProps) {
  const { authToken, signupData } = signupFormProps;
  const navigate = useNavigate();
  const { mutateAsync, isPending, isError, errorMessage } = usePostSignup();
  const { form, onSubmit } = useSignupForm(mutateAsync, signupData, authToken);
  const isDefaultLocation: boolean = useIsDefaultLocation();

  return (
    <Form {...form}>
      <form className={'flex w-full grow flex-col gap-2'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <FormField
          name={'email'}
          control={form.control}
          render={({ field }) => (
            <FormItem className={'gap-1'}>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} type={'email'} placeholder={'example@email.com'} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name={'birthdate'}
          control={form.control}
          render={({ field }) => {
            return (
              <FormItem className={'gap-1'}>
                <FormLabel>
                  <span>Data ur.</span> <small className={'text-muted-foreground text-sm'}>(opcionalne)</small>
                </FormLabel>
                <FormControl>
                  <DatePicker
                    {...field}
                    matcher={{ after: new Date() }}
                    selected={field.value ? new Date(field.value) : null}
                    onSelect={(date) => {
                      if (!date) {
                        field.onChange(null);
                        return;
                      }
                      field.onChange(date.toISOString());
                    }}
                  >
                    <Button type={'button'} variant={'outline'} className={'w-full justify-start gap-2 px-2.5 font-normal'}>
                      <CalendarIcon className={'text-muted-foreground'} />
                      <span className={field.value ? 'text-foreground' : 'text-muted-foreground'}>{field.value ? format(new Date(field.value), 'dd.MM.yyyy') : 'Wybierz datę'}</span>
                    </Button>
                  </DatePicker>
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          name={'phone'}
          control={form.control}
          render={({ field }) => (
            <FormItem className={'gap-1'}>
              <FormLabel>
                <span>Nr. telefonu</span> <small className={'text-muted-foreground text-sm'}>(opcionalne)</small>
              </FormLabel>
              <FormControl>
                <PhoneNumberInput {...field} type={'tel'} onChange={(value) => field.onChange(value || null)} value={field.value ?? undefined} placeholder={'Numer telefonu'} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Alert
          variant={'destructive'}
          data-error={isError}
          className={'overflow-hidden duration-200 data-[error=false]:h-0 data-[error=false]:opacity-0 data-[error=true]:h-auto data-[error=true]:opacity-100'}
        >
          <AlertCircle className={'size-4 shrink-0'} />
          <AlertDescription className={'text-sm'}>{errorMessage.detail}</AlertDescription>
        </Alert>

        <footer className={'mt-auto flex flex-row gap-3'}>
          <Button
            name="back button"
            type={'button'}
            variant={'outline'}
            className={cn(isDefaultLocation ? 'hidden' : 'w-full')}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cofnij
          </Button>
          <Button type={'submit'} className={'w-full'} disabled={isPending}>
            Dalej
          </Button>
        </footer>
      </form>
    </Form>
  );
}
